import React, { useEffect, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { TextField, MenuItem, InputAdornment, Tooltip, Button, Typography, Box, IconButton } from '@mui/material';
import { toast } from 'react-toastify';
import { getBatches, getRepositoryList, deleteBatch } from '../../apis/repository';
import { Navigate, useNavigate } from 'react-router-dom';
import StatusBadge from '../StatusBadge';
import CreateBatchDialog from './CreateBatchDialog';
import { useDebounce } from '../../hooks/useDebounce';
import { VisibilityOutlined, Add, DeleteForeverOutlined } from '@mui/icons-material';
import RepositoryTypeDisplay from './RepositoryTypeDisplay';
import { formatDate } from '../../lib/utils';
import useSubscription from '../../hooks/useSubscription';
import ConfirmationDialog from '../dashboard/ConfirmationDialog';
import { first, isEmpty } from 'lodash';
import TablePagination from '../TablePagination';

const DELETE_CONFIRM_DIALOG_INITIAL_STATE = {
    open: false,
    type: 'danger',
    title: '',
    message: '',
    onConfirm: null,
    loading: false,
};

const BatchList = ({ pageSize = 10 }) => {
    const navigate = useNavigate();
    const [batches, setBatches] = useState([]);
    const [repository, setRepository] = useState(null);
    const [repositories, setRepositories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, _setStatusFilter] = useState('all');
    const [repositoryFilter, setRepositoryFilter] = useState('all');
    const [totalCount, setTotalCount] = useState(0);
    const [paginationModel, setPaginationModel] = useState({
        pageSize,
        page: 0,
    });
    const [createBatchDialogOpen, setCreateBatchDialogOpen] = useState(false);
    const [sortModel, setSortModel] = React.useState(null);
    const [confirmDialog, setConfirmDialog] = useState(DELETE_CONFIRM_DIALOG_INITIAL_STATE);

    // Debounce search to avoid excessive API calls
    const debouncedSearch = useDebounce(search, 500);

    // Load repositories on component mount
    useEffect(() => {
        loadRepositories();
    }, []);

    useEffect(() => {
        loadBatches();
    }, [repositoryFilter, paginationModel.page, paginationModel.pageSize, debouncedSearch, statusFilter, sortModel]);

    const loadRepositories = async () => {
        try {
            // Get first 100 repositories & sort by name in ascending order
            const response = await getRepositoryList(1, 100, null, null, null, { field: 'name', sort: 'asc' });
            setRepositories(response.results || []);
        } catch (error) {
            setRepositories([]);
        }
    };

    const loadBatches = async () => {
        setLoading(true);
        try {
            const response = await getBatches(
                paginationModel.page + 1,
                paginationModel.pageSize,
                repositoryFilter !== 'all' ? repositoryFilter : null,
                statusFilter !== 'all' ? statusFilter : null,
                debouncedSearch,
                sortModel
            );

            // Extract batches from the response
            const batchResults = response.results || [];
            setBatches(batchResults);
            setTotalCount(response.count || 0);

            // Set repository details if available in response
            if (response.repository) {
                setRepository(response.repository);
            }
        } catch (error) {
            toast.error('Failed to load batches');
            setBatches([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    };

    const handleViewBatch = (batchId) => {
        navigate('/batch-management/' + batchId + '/packages');
    };

    const handleDeleteBatch = async (batchId, batchName) => {
        try {
            setConfirmDialog({
                open: true,
                type: 'danger',
                title: 'Delete Batch',
                message: (
                    <Typography>
                        Are you sure you want to delete this batch <strong>{batchName}</strong>?
                    </Typography>
                ),
                onConfirm: async () => {
                    setConfirmDialog((prev) => ({ ...prev, loading: true }));
                    try {
                        await deleteBatch(batchId);
                        setConfirmDialog(DELETE_CONFIRM_DIALOG_INITIAL_STATE);
                        toast.success('Batch deleted successfully');
                        loadBatches();
                    } catch {
                        toast.error('Failed to delete batch');
                        setConfirmDialog((prev) => ({ ...prev, loading: false }));
                    }
                },
                loading: false,
            });
        } catch (error) {
            toast.error('Failed to delete batch');
        }
    };

    const handleCreateBatch = () => {
        setCreateBatchDialogOpen(true);
    };

    const handleCreateBatchSuccess = () => {
        toast.success('Batch created successfully');
        loadBatches(); // Refresh the list
    };

    const columns = [
        {
            field: 'batch_name',
            headerName: 'Batch Name',
            flex: 2,
            renderCell: (params) => <Tooltip title={params?.value}>{params?.value}</Tooltip> || '-',
        },
        {
            field: 'repository_name',
            headerName: 'Repository',
            flex: 1.5,
            renderCell: (params) => <Tooltip title={params?.value}>{params?.value}</Tooltip> || '-',
        },
        {
            field: 'repository_type',
            headerName: 'Repository Type',
            flex: 1.5,
            renderCell: (params) => <RepositoryTypeDisplay type={params?.value} />,
        },
        {
            field: 'bucket',
            headerName: 'Bucket',
            flex: 1.5,
            renderCell: (params) => params?.value || '-',
        },
        {
            field: 'path',
            headerName: 'Path',
            flex: 1.5,
            renderCell: (params) => <Tooltip title={params?.value}>{params?.value}</Tooltip> || '-',
        },
        {
            field: 'status',
            headerName: 'Status',
            flex: 1.5,
            renderCell: (params) => {
                const status = params?.value || '-';
                return <StatusBadge status={status} />;
            },
        },
        {
            field: 'created_at',
            headerName: 'Created',
            flex: 1.5,
            valueFormatter: formatDate,
        },
        {
            field: 'actions',
            headerName: 'Actions',
            flex: 1,
            sortable: false,
            renderCell: (params) => {
                const batch = params?.row;
                if (!batch) return null;

                return (
                    <Box>
                        {/* View */}
                        <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => handleViewBatch(batch.id)} color="primary">
                                <VisibilityOutlined fontSize="medium" />
                            </IconButton>
                        </Tooltip>

                        {/* Delete */}
                        <Tooltip title="Delete Batch">
                            <IconButton
                                size="small"
                                onClick={() => handleDeleteBatch(batch?.id, batch?.batch_name)}
                                color="error"
                            >
                                <DeleteForeverOutlined />
                            </IconButton>
                        </Tooltip>
                    </Box>
                );
            },
        },
    ];

    const { isTrial, isActive, canUploadCourse } = useSubscription();

    // Redirect to pricing if Trial or inactive subscription
    if (isTrial || !isActive) {
        return <Navigate to="/pricing" replace />;
    }

    const handleSortModelChange = async (sortOption) => {
        if (isEmpty(sortOption)) {
            setSortModel(null);
            return;
        }

        const { field, sort } = first(sortOption);
        setSortModel({ field, sort });
    };

    return (
        <Box>
            <Box variant="div" className="custom_table_container">
                <Box variant="div" className="table_filters">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Typography variant="h6" fontWeight={600}>
                            Batch Management
                        </Typography>
                    </Box>
                    <Box className="filter_box">
                        <TextField
                            size="small"
                            placeholder="Search batches..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <span className="material-symbols-rounded">search</span>
                                        </InputAdornment>
                                    ),
                                },
                            }}
                            sx={{ minWidth: 200 }}
                        />

                        {/* Repository Filter - Only show if not filtering by specific repository */}
                        {
                            <TextField
                                select
                                label="Filter by Repository"
                                size="small"
                                value={repositoryFilter}
                                onChange={(e) => setRepositoryFilter(e.target.value)}
                                sx={{ minWidth: 200 }}
                            >
                                <MenuItem value="all">All Repositories</MenuItem>
                                {repositories.map((repo) => (
                                    <MenuItem key={repo.id} value={repo.id}>
                                        {repo.name} ({repo.type})
                                    </MenuItem>
                                ))}
                            </TextField>
                        }

                        {/* <TextField
                            select
                            label="Filter by Status"
                            size="small"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            sx={{ minWidth: 180 }}
                        >
                            <MenuItem value="all">All Status</MenuItem>
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="queued">Queued</MenuItem>
                            <MenuItem value="processing">Processing</MenuItem>
                            <MenuItem value="approved">Validated</MenuItem>
                            <MenuItem value="failed">Failed</MenuItem>
                        </TextField> */}

                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={handleCreateBatch}
                            title="Create new batch"
                            disabled={!canUploadCourse}
                        >
                            Create Batch
                        </Button>
                    </Box>
                </Box>

                <Box variant="div" className="custom_table_responsive">
                    <DataGrid
                        rows={batches}
                        columns={columns}
                        getRowId={(row) => row.id}
                        autoHeight
                        pageSizeOptions={[5, 10, 25, 50]}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        pagination
                        paginationMode="server"
                        loading={loading}
                        disableRowSelectionOnClick
                        disableColumnMenu
                        localeText={{
                            noRowsLabel: 'No Batches Found',
                            sortLabel: '',
                        }}
                        rowCount={totalCount}
                        sortingMode="server"
                        onSortModelChange={handleSortModelChange}
                        slots={{
                            pagination: () => (
                                <TablePagination
                                    paginationModel={paginationModel}
                                    onPaginationModelChange={setPaginationModel}
                                    totalCount={totalCount}
                                    pageSizeOptions={[5, 10, 25, 50]}
                                />
                            ),
                        }}
                    />
                </Box>
            </Box>

            <CreateBatchDialog
                open={createBatchDialogOpen}
                onClose={() => setCreateBatchDialogOpen(false)}
                onSuccess={handleCreateBatchSuccess}
                repository={repository}
            />

            <ConfirmationDialog
                open={confirmDialog.open}
                onClose={() => setConfirmDialog(DELETE_CONFIRM_DIALOG_INITIAL_STATE)}
                onConfirm={confirmDialog.onConfirm}
                title={confirmDialog.title}
                message={confirmDialog.message}
                type={confirmDialog.type}
                loading={confirmDialog.loading}
                confirmText={'Delete'}
            />
        </Box>
    );
};

export default BatchList;
